import { Clock, Sparkles } from "lucide-react";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const TOOLTIP_STYLE = {
  background: "#0f1a2e",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 12,
  fontSize: 12,
  color: "#e2e8f0",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
};

const SubChart = ({ title, data, dataKey1, dataKey2, label1, label2 }) => (
  <div>
    <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-3 font-semibold">{title}</div>
    <div style={{ height: 180 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis dataKey="year" tick={{ fill: "#94a3b8", fontSize: 10 }} stroke="rgba(255,255,255,0.08)" />
          <YAxis tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.08)" />
          <Tooltip contentStyle={{ ...TOOLTIP_STYLE, fontSize: 11 }} />
          <Line type="monotone" dataKey={dataKey1} name={label1} stroke="#10b981" strokeWidth={2} dot={{ fill: "#10b981", r: 3 }} />
          <Line type="monotone" dataKey={dataKey2} name={label2} stroke="#8b5cf6" strokeWidth={2} dot={{ fill: "#8b5cf6", r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const EvolutionHighlights = ({ timeMachine }) => {
  if (!timeMachine.timeline || timeMachine.timeline.length <= 1) return null;
  const first = timeMachine.timeline[0];
  const last = timeMachine.timeline[timeMachine.timeline.length - 1];
  const a1 = first.item1, a2 = last.item1, b1 = first.item2, b2 = last.item2;
  const aGrowth = a1.total > 0 ? Math.round(((a2.total - a1.total) / a1.total) * 100) : 0;
  const bGrowth = b1.total > 0 ? Math.round(((b2.total - b1.total) / b1.total) * 100) : 0;
  const aAicciDelta = (a2.aicci - a1.aicci).toFixed(1);
  const bAicciDelta = (b2.aicci - b1.aicci).toFixed(1);
  const aFasterGrowth = parseFloat(aAicciDelta) > parseFloat(bAicciDelta);
  const bFasterGrowth = parseFloat(bAicciDelta) > parseFloat(aAicciDelta);

  const fmtDelta = (v) => `${v >= 0 ? "+" : ""}${v}`;

  return (
    <div className="mt-4 space-y-2">
      <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-2">Evolution Highlights (2020→2026)</div>
      <div className="flex items-start gap-2 text-xs text-slate-300">
        <span className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
        <span><strong className="text-emerald-400">{timeMachine.item1_label}</strong>: {aGrowth}% growth, AICCI {fmtDelta(aAicciDelta)} pts</span>
      </div>
      <div className="flex items-start gap-2 text-xs text-slate-300">
        <span className="w-2 h-2 rounded-full bg-violet-400 mt-1.5 flex-shrink-0" />
        <span><strong className="text-violet-400">{timeMachine.item2_label}</strong>: {bGrowth}% growth, AICCI {fmtDelta(bAicciDelta)} pts</span>
      </div>
      {aFasterGrowth && (
        <div className="flex items-start gap-2 text-xs text-amber-400/80 mt-1">
          <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{timeMachine.item1_label}'s consciousness is growing faster — a signal of shifting values in AI development</span>
        </div>
      )}
      {bFasterGrowth && (
        <div className="flex items-start gap-2 text-xs text-amber-400/80 mt-1">
          <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{timeMachine.item2_label}'s consciousness is growing faster — a signal of shifting values in AI development</span>
        </div>
      )}
    </div>
  );
};

export const TimeMachine = ({ timeMachine }) => {
  if (!timeMachine) return null;
  return (
    <div className="animate-fade-in space-y-4 mt-2" data-testid="time-machine-section">
      <div className="bg-gradient-to-r from-amber-500/[0.05] via-violet-500/[0.05] to-emerald-500/[0.05] border border-white/[0.08] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <Clock className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-bold text-slate-200 uppercase tracking-widest">Time Machine</h3>
          <span className="text-[10px] text-slate-400 ml-1">How did AI consciousness evolve?</span>
        </div>

        {/* AICCI Evolution Chart */}
        <div className="mb-6">
          <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-3 font-semibold">AICCI Consciousness Score Over Time</div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeMachine.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="year" tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 600 }} stroke="rgba(255,255,255,0.08)" />
                <YAxis domain={[0, 60]} tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.08)" />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Line type="monotone" dataKey="item1.aicci" name={`${timeMachine.item1_label} AICCI`}
                  stroke="#10b981" strokeWidth={3} dot={{ fill: "#10b981", r: 5, strokeWidth: 0 }}
                  activeDot={{ r: 7, fill: "#10b981", stroke: "#0f172a", strokeWidth: 3 }} />
                <Line type="monotone" dataKey="item2.aicci" name={`${timeMachine.item2_label} AICCI`}
                  stroke="#8b5cf6" strokeWidth={3} dot={{ fill: "#8b5cf6", r: 5, strokeWidth: 0 }}
                  activeDot={{ r: 7, fill: "#8b5cf6", stroke: "#0f172a", strokeWidth: 3 }} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SubChart
            title="Total AI Products"
            data={timeMachine.timeline}
            dataKey1="item1.total"
            dataKey2="item2.total"
            label1={timeMachine.item1_label}
            label2={timeMachine.item2_label}
          />
          <SubChart
            title="Care Orientation %"
            data={timeMachine.timeline}
            dataKey1="item1.care_pct"
            dataKey2="item2.care_pct"
            label1={timeMachine.item1_label}
            label2={timeMachine.item2_label}
          />
        </div>

        <EvolutionHighlights timeMachine={timeMachine} />
      </div>
    </div>
  );
};

export default TimeMachine;
